import { Component, ChangeDetectorRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CompositeKey, LogError } from '@memberjunction/core';
import { NavigationService } from '@memberjunction/ng-shared';

import {
    ConduitActionClient,
    ConduitBinding,
    ConduitAudiencePreview,
    ConduitReconcileResult,
    ConduitSyncActivitiesResult,
    ConduitActivityRow,
    ConduitHealthReport,
    ConduitReplySequence,
    ConduitContactView,
} from '../../services/conduit-action.client';

/** The eight Conduit screens, mirroring the approved mockup's IA. */
type ConduitScreen =
    | 'bindings'
    | 'views'
    | 'builder'
    | 'bind'
    | 'dryrun'
    | 'history'
    | 'activities'
    | 'health';

/**
 * Which slice of Conduit this mounted instance owns. The Outbound Dashboard
 * mounts `obd-conduit` once per re-homed tab and passes the matching section,
 * so each instance shows only its own screens (no internal top-level nav).
 *   - 'audiences'  → Contact views list + View builder (Smart Filter)
 *   - 'bindings'   → Bindings list → Bind → Dry run → Run history
 *   - 'health'     → Integration health
 *   - 'activities' → Replies → Activities (folded into the People tab)
 */
export type ConduitSection = 'audiences' | 'bindings' | 'health' | 'activities';

/** The default screen each section opens on. */
const SECTION_DEFAULT_SCREEN: Record<ConduitSection, ConduitScreen> = {
    audiences: 'views',
    bindings: 'bindings',
    health: 'health',
    activities: 'activities',
};

/** Per-screen load state. 'not-deployed' is first-class (actions not synced). */
type ScreenStatus = 'idle' | 'loading' | 'ready' | 'not-deployed' | 'error';

interface ScreenState {
    status: ScreenStatus;
    message: string;
}

const IDLE: ScreenState = { status: 'idle', message: '' };

const FREQ_OPTIONS: ReadonlyArray<{ hours: number; title: string; detail: string }> = [
    { hours: 24, title: 'Daily', detail: 'once per day · default' },
    { hours: 6, title: 'Every 6 h', detail: '4× per day' },
    { hours: 1, title: 'Hourly', detail: 'fast-moving' },
    { hours: 0, title: 'Manual', detail: 'you trigger it' },
];

/**
 * Conduit — the AIDP→Reply outbound UX, re-homed as a child component INSIDE the
 * Outbound Dashboard's tab strip. A marketer defines an audience (MJ Smart
 * Filter), binds it to a Reply sequence, dry-runs the diff, goes live behind a
 * deliberate gate, watches lockstep, sees replies, and monitors integrations.
 *
 * This component is NOT a standalone dashboard. The dashboard owns the global
 * brand selector and passes {@link companyId} / {@link companyName} as inputs,
 * and {@link section} decides which slice of screens this instance renders.
 * All server calls go through {@link ConduitActionClient}; nothing sends without
 * the Dry Run → Approve gate.
 */
@Component({
    standalone: false,
    selector: 'obd-conduit',
    templateUrl: './conduit.component.html',
    styleUrls: ['../../styles/outbound-tokens.css', './conduit.component.css'],
    providers: [ConduitActionClient],
})
export class ConduitComponent implements OnInit, OnChanges {
    private cdr = inject(ChangeDetectorRef);
    private client = inject(ConduitActionClient);
    private navigation = inject(NavigationService);

    /**
     * Open a saved view in the core MJ Data Explorer. Uses NavigationService
     * (never the router directly) so the view opens in the standard MJ entity
     * record surface where Jared can inspect/verify the generated SQL and run
     * it — per the directive to lean on (and pressure-test) the core viewing UX.
     */
    openViewInDataExplorer(viewId: string): void {
        if (!viewId) return;
        this.navigation.OpenEntityRecord('User Views', CompositeKey.FromID(viewId));
    }

    // ─── Inputs (the dashboard owns the brand selector + tab routing) ────────
    /** The dashboard's globally-selected brand (MJ Company id). '' = no brand. */
    @Input() companyId = '';
    /** The selected brand's display name (for in-screen copy). */
    @Input() companyName = '';
    /** Which slice of Conduit this instance renders. */
    @Input() section: ConduitSection = 'bindings';

    // ─── Navigation (internal screen routing within a section's flow) ────────
    screen: ConduitScreen = 'bindings';

    get selectedCompanyId(): string {
        return this.companyId;
    }
    get selectedCompanyName(): string {
        return this.companyName;
    }

    /** Back-to-list affordance label for the active section's internal flow. */
    get sectionListLabel(): string {
        return this.section === 'audiences' ? 'Contact views' : 'Bindings';
    }
    get sectionListScreen(): ConduitScreen {
        return this.section === 'audiences' ? 'views' : 'bindings';
    }

    /** True when the current screen is a drill-in (shows a back link to the list). */
    get showBackToList(): boolean {
        if (this.section === 'audiences') return this.screen === 'builder';
        if (this.section === 'bindings') return this.screen === 'bind' || this.screen === 'dryrun' || this.screen === 'history';
        return false;
    }

    go(screen: ConduitScreen): void {
        this.screen = screen;
        // Lazy-load each screen the first time it's shown.
        switch (screen) {
            case 'bindings': if (this.bindingsState.status === 'idle') void this.loadBindings(); break;
            case 'views':
                if (this.bindingsState.status === 'idle') void this.loadBindings();
                if (this.contactViewsState.status === 'idle') void this.loadContactViews();
                break;
            case 'bind':
                if (this.bindSequencesState.status === 'idle') void this.loadBindSequences();
                if (this.contactViewsState.status === 'idle') void this.loadContactViews();
                break;
            case 'activities': if (this.activitiesState.status === 'idle') void this.loadActivities(); break;
            case 'health':   if (this.healthState.status === 'idle') void this.loadHealth(); break;
            default: break;
        }
        this.cdr.detectChanges();
    }

    // ─── Bindings (drives the Bindings screen) ──────────────────────────────
    bindingsState: ScreenState = IDLE;
    bindings: ConduitBinding[] = [];

    // ─── Saved contact views (Audiences list + Bind-screen picker) ──────────
    contactViewsState: ScreenState = IDLE;
    contactViews: ConduitContactView[] = [];

    /**
     * Saved contact views for the Audiences tab. Drawn from the
     * 'Outbound: List Contact Views' action (not derived from bindings), with the
     * binding count layered in so the table can still show "used by N binding(s)".
     */
    get distinctViews(): Array<{ viewId: string; view: string; bindingCount: number; total: number | null }> {
        const bindingCountByView = new Map<string, number>();
        for (const b of this.bindings) {
            if (!b.viewId) continue;
            bindingCountByView.set(b.viewId, (bindingCountByView.get(b.viewId) ?? 0) + 1);
        }
        return this.contactViews.map(v => ({
            viewId: v.viewId,
            view: v.name || v.viewId,
            bindingCount: bindingCountByView.get(v.viewId) ?? 0,
            total: null,
        }));
    }

    // ─── View builder (Smart Filter) ────────────────────────────────────────
    builderName = '';
    builderPrompt = '';
    builderViewId = '';            // set when editing an existing view
    builderSaving = false;
    builderPreviewing = false;
    builderError = '';
    builderPreview: ConduitAudiencePreview | null = null;
    builderGeneratedSql = '';      // the most recent AI-generated/preview clause (reference)
    builderEditableSql = '';       // the operator-editable clause (textarea model) — what gets saved
    builderExplanation = '';
    builderSavedViewId = '';       // populated after a successful save
    showSql = false;               // the "Verify SQL" disclosure (collapsed by default)
    builderGenerated = false;      // true once a draft clause exists (enables Save + edit pane)

    /** True when the operator has edited the clause away from what the AI produced. */
    get builderSqlEdited(): boolean {
        return this.builderEditableSql.trim() !== this.builderGeneratedSql.trim();
    }

    // ─── Bind to sequence ───────────────────────────────────────────────────
    bindViewId = '';
    bindSequenceId: number | null = null;       // chosen Reply sequence id
    bindName = '';                              // optional binding display name
    bindFrequencyHours = 24;
    bindRemovalMode: 'Finish' | 'Pause' | 'Remove' = 'Pause';
    bindAddOnMatch = true;
    bindRemoveOnLeave = true;
    readonly freqOptions = FREQ_OPTIONS;
    readonly removalModes: ReadonlyArray<'Finish' | 'Pause' | 'Remove'> = ['Finish', 'Pause', 'Remove'];
    // Reply-sequence picker (populated via Get Reply Sequences).
    bindSequences: ConduitReplySequence[] = [];
    bindSequencesState: ScreenState = IDLE;
    bindCreating = false;
    bindError = '';

    get bindSelectedSequence(): ConduitReplySequence | null {
        return this.bindSequences.find(s => s.id === this.bindSequenceId) ?? null;
    }

    // ─── Dry run ────────────────────────────────────────────────────────────
    dryRunState: ScreenState = IDLE;
    dryRunBinding: ConduitBinding | null = null;
    dryRunResult: ConduitReconcileResult | null = null;
    isApproving = false;
    approveMessage = '';

    // ─── Replies → Activities ───────────────────────────────────────────────
    activitiesState: ScreenState = IDLE;
    activities: ConduitActivityRow[] = [];
    syncResult: ConduitSyncActivitiesResult | null = null;
    isSyncing = false;

    // ─── Integration health ─────────────────────────────────────────────────
    healthState: ScreenState = IDLE;
    health: ConduitHealthReport | null = null;
    stalenessThresholdHours = 48;

    // ─── Lifecycle ──────────────────────────────────────────────────────────

    ngOnInit(): void {
        // Open on this section's default screen and load it.
        this.go(SECTION_DEFAULT_SCREEN[this.section]);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['section'] && !changes['section'].firstChange) {
            // The host re-used this instance for a different section — reset.
            this.resetForBrandChange();
            this.go(SECTION_DEFAULT_SCREEN[this.section]);
        } else if (changes['companyId'] && !changes['companyId'].firstChange) {
            // The dashboard's global brand selector changed — re-scope screens.
            this.resetForBrandChange();
            this.go(SECTION_DEFAULT_SCREEN[this.section]);
        }
    }

    /** Re-scope the data-bearing screens to the (new) brand / section. */
    private resetForBrandChange(): void {
        this.bindingsState = IDLE;
        this.contactViewsState = IDLE;
        this.contactViews = [];
        this.activitiesState = IDLE;
        this.bindSequencesState = IDLE;
        this.healthState = IDLE;
        this.bindSequenceId = null;
        this.bindSequences = [];
        this.dryRunBinding = null;
        this.dryRunResult = null;
        this.builderViewId = '';
        this.builderSavedViewId = '';
        this.builderPreview = null;
    }

    // ─── Bindings ───────────────────────────────────────────────────────────

    async loadBindings(): Promise<void> {
        this.bindingsState = { status: 'loading', message: '' };
        try {
            const outcome = await this.client.listBindings(this.selectedCompanyId || undefined);
            if (outcome.kind === 'not-deployed') {
                this.bindingsState = { status: 'not-deployed', message: outcome.message };
            } else if (outcome.kind === 'error') {
                this.bindingsState = { status: 'error', message: outcome.message };
            } else {
                this.bindings = outcome.data;
                this.bindingsState = { status: 'ready', message: '' };
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] loadBindings: ${msg}`);
            this.bindingsState = { status: 'error', message: msg };
        } finally {
            this.cdr.detectChanges();
        }
    }

    /** Load the saved contact views for the Audiences list + Bind-screen picker. */
    async loadContactViews(): Promise<void> {
        this.contactViewsState = { status: 'loading', message: '' };
        try {
            const outcome = await this.client.listContactViews();
            if (outcome.kind === 'not-deployed') {
                this.contactViewsState = { status: 'not-deployed', message: outcome.message };
            } else if (outcome.kind === 'error') {
                this.contactViewsState = { status: 'error', message: outcome.message };
            } else {
                this.contactViews = outcome.data;
                this.contactViewsState = { status: 'ready', message: '' };
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] loadContactViews: ${msg}`);
            this.contactViewsState = { status: 'error', message: msg };
        } finally {
            this.cdr.detectChanges();
        }
    }

    /** Lockstep summary across the loaded bindings (audience vs. last-synced). */
    get lockstepSummary(): { inViews: number; pending: number; liveBindings: number } {
        let inViews = 0;
        let pending = 0;
        let liveBindings = 0;
        for (const b of this.bindings) {
            const added = b.lastSyncAddedCount ?? 0;
            const removed = b.lastSyncRemovedCount ?? 0;
            inViews += added; // best-effort: last applied adds as a proxy for "in views"
            pending += Math.abs(added - removed) > 0 ? 0 : 0; // placeholder; no per-run pending in v1
            if (b.mode === 'Live' && b.isActive) liveBindings++;
        }
        return { inViews, pending, liveBindings };
    }

    // ─── View builder ───────────────────────────────────────────────────────

    openBuilderForNew(): void {
        this.builderName = '';
        this.builderPrompt = '';
        this.builderViewId = '';
        this.builderSavedViewId = '';
        this.builderPreview = null;
        this.builderGeneratedSql = '';
        this.builderEditableSql = '';
        this.builderExplanation = '';
        this.builderError = '';
        this.showSql = false;
        this.builderGenerated = false;
        this.go('builder');
    }

    /**
     * Generate (or re-generate) the draft clause + match preview from the prompt.
     * This is the "Generate" step: it asks the server to produce the WHERE clause
     * and the "who matches" count, then drops the clause into the editable pane so
     * the operator can review/tweak it before saving.
     */
    async generateBuilderDraft(): Promise<void> {
        this.builderError = '';
        this.builderPreviewing = true;
        try {
            const outcome = this.builderViewId
                ? await this.client.previewAudience({ viewId: this.builderViewId })
                : await this.client.previewAudience({ smartFilterPrompt: this.builderPrompt });
            if (outcome.kind === 'success') {
                this.builderPreview = outcome.data;
                if (outcome.data.generatedWhereClause) {
                    this.builderGeneratedSql = outcome.data.generatedWhereClause;
                    // Seed the editable pane with the freshly-generated clause.
                    this.builderEditableSql = outcome.data.generatedWhereClause;
                    this.builderGenerated = true;
                    this.showSql = true; // surface the verify/edit pane after generating
                }
            } else {
                this.builderError = outcome.message;
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] generateBuilderDraft: ${msg}`);
            this.builderError = msg;
        } finally {
            this.builderPreviewing = false;
            this.cdr.detectChanges();
        }
    }

    /**
     * Re-preview the audience against the CURRENT editable clause text so the
     * operator can see "who matches" after tweaking the WHERE clause. Falls back
     * to preview-by-prompt if the clause pane is empty.
     */
    async previewEditedClause(): Promise<void> {
        this.builderError = '';
        this.builderPreviewing = true;
        try {
            const clause = this.builderEditableSql.trim();
            const outcome = clause.length > 0
                ? await this.client.previewAudience({ whereClause: clause })
                : await this.client.previewAudience({ smartFilterPrompt: this.builderPrompt });
            if (outcome.kind === 'success') {
                this.builderPreview = outcome.data;
            } else {
                this.builderError = outcome.message;
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] previewEditedClause: ${msg}`);
            this.builderError = msg;
        } finally {
            this.builderPreviewing = false;
            this.cdr.detectChanges();
        }
    }

    async saveBuilderView(): Promise<void> {
        this.builderError = '';
        this.builderSaving = true;
        try {
            // Pass the (possibly operator-edited) clause. When it equals the
            // AI-generated clause this is a no-op override; when the operator
            // tweaked it the action stores the edited clause verbatim.
            const editedClause = this.builderEditableSql.trim() || undefined;
            const outcome = await this.client.saveContactView(
                this.builderName,
                this.builderPrompt,
                this.builderViewId || undefined,
                editedClause,
            );
            if (outcome.kind === 'success') {
                this.builderSavedViewId = outcome.data.viewId;
                this.builderViewId = outcome.data.viewId;
                this.builderGeneratedSql = outcome.data.generatedWhereClause;
                this.builderEditableSql = outcome.data.generatedWhereClause;
                this.builderExplanation = outcome.data.explanation;
                this.builderGenerated = true;
                // Refresh the preview against the now-saved view, and the saved-views
                // list so the new view appears in the Audiences tab + Bind picker.
                await this.generateBuilderDraft();
                if (this.contactViewsState.status !== 'idle') await this.loadContactViews();
            } else {
                this.builderError = outcome.message;
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] saveBuilderView: ${msg}`);
            this.builderError = msg;
        } finally {
            this.builderSaving = false;
            this.cdr.detectChanges();
        }
    }

    // ─── Bind to sequence ─────────────────────────────────────────────────────

    /** Load the brand's Reply sequences into the picker. */
    async loadBindSequences(): Promise<void> {
        this.bindError = '';
        if (!this.selectedCompanyId) {
            this.bindSequencesState = { status: 'error', message: 'Pick a brand first to load its Reply sequences.' };
            this.cdr.detectChanges();
            return;
        }
        this.bindSequencesState = { status: 'loading', message: '' };
        try {
            const outcome = await this.client.listReplySequences(this.selectedCompanyId);
            if (outcome.kind === 'success') {
                this.bindSequences = outcome.data;
                this.bindSequencesState = { status: 'ready', message: '' };
            } else if (outcome.kind === 'not-deployed') {
                this.bindSequencesState = { status: 'not-deployed', message: outcome.message };
            } else {
                this.bindSequencesState = { status: 'error', message: outcome.message };
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] loadBindSequences: ${msg}`);
            this.bindSequencesState = { status: 'error', message: msg };
        } finally {
            this.cdr.detectChanges();
        }
    }

    /**
     * Persist the binding (DryRun by default), then route to its Dry Run so the
     * operator previews the per-contact diff before going live. This is the
     * full Bind flow living in the app — nothing is enrolled here.
     */
    async createBindingAndDryRun(): Promise<void> {
        this.bindError = '';
        if (!this.selectedCompanyId) { this.bindError = 'Pick a brand first.'; return; }
        if (!this.bindViewId) { this.bindError = 'Pick a saved view (build one first).'; return; }
        if (this.bindSequenceId == null) { this.bindError = 'Pick a Reply sequence to bind to.'; return; }
        this.bindCreating = true;
        try {
            const seq = this.bindSelectedSequence;
            const outcome = await this.client.createBinding({
                companyId: this.selectedCompanyId,
                viewId: this.bindViewId,
                externalSequenceId: this.bindSequenceId,
                externalSequenceName: seq?.name,
                name: this.bindName.trim() || undefined,
                removalMode: this.bindRemovalMode,
                syncFrequencyHours: this.bindFrequencyHours,
            });
            if (outcome.kind !== 'success') {
                this.bindError = outcome.message;
                return;
            }
            // Refresh bindings, then open the new binding's Dry Run.
            await this.loadBindings();
            const created = this.bindings.find(b => b.id === outcome.data.bindingId);
            if (created) {
                await this.openDryRun(created);
            } else {
                // Fallback: still route to the dry-run screen on the bindings refresh.
                this.go('bindings');
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] createBindingAndDryRun: ${msg}`);
            this.bindError = msg;
        } finally {
            this.bindCreating = false;
            this.cdr.detectChanges();
        }
    }

    // ─── Dry run / approve ──────────────────────────────────────────────────

    async openDryRun(binding: ConduitBinding): Promise<void> {
        this.dryRunBinding = binding;
        this.dryRunResult = null;
        this.approveMessage = '';
        this.go('dryrun');
        await this.runDryRun();
    }

    async runDryRun(): Promise<void> {
        if (!this.dryRunBinding) return;
        this.dryRunState = { status: 'loading', message: '' };
        try {
            const outcome = await this.client.reconcileBinding(this.dryRunBinding.id);
            if (outcome.kind === 'success') {
                this.dryRunResult = outcome.data;
                this.dryRunState = { status: 'ready', message: '' };
            } else if (outcome.kind === 'not-deployed') {
                this.dryRunState = { status: 'not-deployed', message: outcome.message };
            } else {
                this.dryRunState = { status: 'error', message: outcome.message };
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] runDryRun: ${msg}`);
            this.dryRunState = { status: 'error', message: msg };
        } finally {
            this.cdr.detectChanges();
        }
    }

    /** True when the binding is in DryRun mode (the dry run shows would-changes). */
    get dryRunIsPreview(): boolean {
        return this.dryRunBinding?.mode === 'DryRun';
    }

    /**
     * The deliberate go-live gate: flip the binding to Live, then run the
     * reconcile so the changes apply to Reply. This is the ONLY path that sets a
     * binding Live + touches Reply.
     */
    async approveAndGoLive(): Promise<void> {
        if (!this.dryRunBinding) return;
        this.isApproving = true;
        this.approveMessage = '';
        try {
            const flip = await this.client.setBindingMode(this.dryRunBinding.id, 'Live');
            if (!flip.success) {
                this.approveMessage = `Could not set binding live: ${flip.message}`;
                return;
            }
            this.dryRunBinding = { ...this.dryRunBinding, mode: 'Live' };
            const outcome = await this.client.reconcileBinding(this.dryRunBinding.id);
            if (outcome.kind === 'success') {
                this.dryRunResult = outcome.data;
                this.approveMessage = outcome.data.message;
                await this.loadBindings();
                this.go('history');
            } else {
                this.approveMessage = outcome.kind === 'not-deployed'
                    ? `Reconcile action not deployed: ${outcome.message}`
                    : outcome.message;
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] approveAndGoLive: ${msg}`);
            this.approveMessage = msg;
        } finally {
            this.isApproving = false;
            this.cdr.detectChanges();
        }
    }

    // ─── Replies → Activities ───────────────────────────────────────────────

    async loadActivities(): Promise<void> {
        this.activitiesState = { status: 'loading', message: '' };
        try {
            const outcome = await this.client.listReplyActivities();
            if (outcome.kind === 'success') {
                this.activities = outcome.data;
                this.activitiesState = { status: 'ready', message: '' };
            } else if (outcome.kind === 'not-deployed') {
                this.activitiesState = { status: 'not-deployed', message: outcome.message };
            } else {
                this.activitiesState = { status: 'error', message: outcome.message };
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] loadActivities: ${msg}`);
            this.activitiesState = { status: 'error', message: msg };
        } finally {
            this.cdr.detectChanges();
        }
    }

    async syncRepliesNow(dryRun: boolean): Promise<void> {
        this.isSyncing = true;
        try {
            const outcome = await this.client.syncReplyActivities(this.selectedCompanyId || undefined, dryRun);
            if (outcome.kind === 'success') {
                this.syncResult = outcome.data;
                if (!dryRun) await this.loadActivities();
            } else {
                this.activitiesState = { status: 'error', message: outcome.message };
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] syncRepliesNow: ${msg}`);
            this.activitiesState = { status: 'error', message: msg };
        } finally {
            this.isSyncing = false;
            this.cdr.detectChanges();
        }
    }

    // ─── Integration health ─────────────────────────────────────────────────

    async loadHealth(): Promise<void> {
        this.healthState = { status: 'loading', message: '' };
        try {
            const outcome = await this.client.getIntegrationHealth(this.stalenessThresholdHours);
            if (outcome.kind === 'success') {
                this.health = outcome.data;
                this.healthState = { status: 'ready', message: '' };
            } else if (outcome.kind === 'not-deployed') {
                this.healthState = { status: 'not-deployed', message: outcome.message };
            } else {
                this.healthState = { status: 'error', message: outcome.message };
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[Conduit] loadHealth: ${msg}`);
            this.healthState = { status: 'error', message: msg };
        } finally {
            this.cdr.detectChanges();
        }
    }

    // ─── Small view helpers ─────────────────────────────────────────────────

    /** Initials for an avatar bubble. */
    initials(name: string): string {
        const parts = name.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    /** Map a binding's last-sync status to a chip CSS modifier. */
    statusChip(status: string | null): 'ok' | 'warn' | 'bad' | 'paused' {
        switch ((status ?? '').toLowerCase()) {
            case 'success': return 'ok';
            case 'partialfailure': return 'warn';
            case 'failed': return 'bad';
            default: return 'paused';
        }
    }

    healthChip(h: ConduitIntegrationHealthLike): 'ok' | 'warn' | 'bad' {
        if (h.errored) return 'bad';
        if (h.isStale) return 'warn';
        return 'ok';
    }

    freqLabel(hours: number | null): string {
        if (hours === null) return '—';
        if (hours === 0) return 'Manual';
        if (hours === 1) return 'Hourly';
        if (hours === 24) return 'Daily';
        return `Every ${hours} h`;
    }
}

/** Local structural type so healthChip can be reused without importing the iface twice. */
interface ConduitIntegrationHealthLike { errored: boolean; isStale: boolean; }
